<!--
  CollectorShopMoney — the shop's money in the sticky shop header (collector-
  clarity, handoff spec Section 6.5; mockup pair C).

  Three figures that always add up to what the shop needs:
    paid       what his marks say he paid (`shop.spentCents`, the backend's figure)
    remaining  the lines still to buy, net of her deposit at the shop
    gap        the rest — not bought, bought fewer or cheaper (red), or paid
               above the list (amber, «دفعت زيادة»)
  and one advisory line when the cash on him does not cover what is left —
  only in a shop he is still working (one he closed for the leader needs no
  cash from him), and only when the cash is known (`cashWithMeCents` is null
  when the leader recorded no allocation).

  A white strip inside the indigo header, so it stays on screen however far he
  scrolls. Nothing here is summed by hand: every figure comes from
  collector/moneyView.js `shopMoney` over the day document, which the action
  layer rebases on every tap — so the strip moves with a mark and has no code
  of its own for it. Spans and divs only; one `role="group"` with the whole
  sentence as its label, the figures hidden from a screen reader.
-->
<template>
  <div class="c-smoney" role="group" :aria-label="ariaLabel">
    <div class="c-smoney__row" aria-hidden="true">
      <div class="c-smoney__cell">
        <span class="c-smoney__lab">{{ t('collector.shopMoney.paid') }}</span>
        <span class="c-smoney__big c-num">{{ pounds(money.paidCents) }} <small>{{ unit }}</small></span>
      </div>
      <div class="c-smoney__cell c-smoney__cell--end">
        <span class="c-smoney__lab">{{ t('collector.shopMoney.planned') }}</span>
        <span class="c-smoney__mid c-num">{{ pounds(money.plannedCents) }} <small>{{ unit }}</small></span>
      </div>
    </div>

    <div v-if="money.plannedCents > 0" class="c-smoney__bar" aria-hidden="true">
      <i v-if="money.segments.paid > 0" class="c-smoney__seg c-smoney__seg--paid" :style="{ width: `${money.segments.paid}%` }" />
      <i v-if="money.segments.gap > 0" class="c-smoney__seg c-smoney__seg--gap" :style="{ width: `${money.segments.gap}%` }" />
    </div>

    <div class="c-smoney__legend c-num" aria-hidden="true">
      <span class="c-smoney__key">
        <i class="c-smoney__dot c-smoney__dot--rest" />
        <i18n-t keypath="collector.shopMoney.remaining" tag="span" scope="global">
          <template #amount>
            <b>{{ pounds(money.remainingCents) }}</b>
          </template>
        </i18n-t>
      </span>
      <span v-if="money.gapCents > 0" class="c-smoney__key">
        <i class="c-smoney__dot c-smoney__dot--gap" />
        <i18n-t keypath="collector.shopMoney.notBought" tag="span" scope="global">
          <template #amount>
            <b>{{ pounds(money.gapCents) }}</b>
          </template>
        </i18n-t>
      </span>
      <span v-else-if="money.gapCents < 0" class="c-smoney__key">
        <i class="c-smoney__dot c-smoney__dot--over" />
        <i18n-t keypath="collector.shopMoney.over" tag="span" scope="global">
          <template #amount>
            <b>{{ pounds(-money.gapCents) }}</b>
          </template>
        </i18n-t>
      </span>
    </div>

    <div v-if="shortCents > 0" class="c-smoney__short c-num">
      {{ t('collector.shopMoney.short', { cash: pounds(cashWithMeCents), short: pounds(shortCents) }) }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { I18nT, useI18n } from 'vue-i18n';
import { isOpenShop, shopMoney } from '@/collector/moneyView.js';
import { useCurrency } from '@/composables/useCurrency.js';

const props = defineProps({
  /** The shop, live from the day document. */
  shop: { type: Object, default: null },
  /** `cash.withMeCents` right now (advisory); null when the cash is not known. */
  cashWithMeCents: { type: Number, default: null },
});

const { t } = useI18n();
const { formatAmount } = useCurrency();
// Nothing is floored, so a figure can go below zero: the sign is U+2212 and
// stays on its digits (Intl keeps it inside its LRM).
const pounds = (cents) => formatAmount(cents, { trimZeros: true }).replace('-', '−');
const unit = computed(() => t('collector.cash.unit'));

const money = computed(() => shopMoney(props.shop));
/** Only when what is left HERE costs more than the cash he holds — in a shop still his, with the cash known. */
const shortCents = computed(() => {
  if (props.cashWithMeCents == null || !isOpenShop(props.shop)) return 0;
  return Math.max(0, money.value.remainingCents - props.cashWithMeCents);
});

const ariaLabel = computed(() =>
  t('collector.shopMoney.aria', {
    paid: pounds(money.value.paidCents),
    planned: pounds(money.value.plannedCents),
    remaining: pounds(money.value.remainingCents),
  }),
);
</script>

<style scoped>
.c-smoney {
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: var(--c-radius-button);
  background: var(--c-card);
  color: var(--c-ink);
}

.c-smoney__row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.c-smoney__cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.c-smoney__cell--end { align-items: flex-end; }

.c-smoney__lab {
  font-size: 12px;
  font-weight: 600;
  color: var(--c-muted);
}

.c-smoney__big {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.05;
  color: var(--c-green-text);
}

.c-smoney__mid {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
}

.c-smoney__big small,
.c-smoney__mid small {
  font-size: 12px;
  font-weight: 500;
  color: var(--c-muted);
}

.c-smoney__bar {
  display: flex;
  gap: 2px;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--c-border);
}

.c-smoney__seg {
  display: block;
  height: 100%;
}

.c-smoney__seg--paid { background: var(--c-green-text); }
.c-smoney__seg--gap { background: var(--c-red-text); }

.c-smoney__legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 6px 12px;
  font-size: 13px;
  color: var(--c-ink-soft);
}

.c-smoney__key {
  display: inline-flex;
  align-items: center;
}

/* the amounts are the figures he reads; the words around them step back */
.c-smoney__key b {
  font-weight: 700;
  color: var(--c-ink);
}

.c-smoney__dot {
  display: inline-block;
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-inline-end: 5px;
  border-radius: 50%;
}

.c-smoney__dot--rest { background: var(--c-dot); }
.c-smoney__dot--gap { background: var(--c-red-text); }
.c-smoney__dot--over { background: var(--c-amber-pill); }

.c-smoney__short {
  padding: 7px 12px;
  border-radius: var(--c-radius-chip);
  background: var(--c-amber-fill);
  color: var(--c-amber-text);
  font-size: 13px;
  font-weight: 700;
}
</style>
